import React from 'react';
import { Field, FieldArray } from 'redux-form';
import { months } from '../../../../Utils/months';
import { 
    AddTransactionButtons, 
    separateFieldsByType, 
    renderCollapsibleSectionHeader,
    renderSectionTotalRow,
    renderDifferenceRow,
    formatComparativoCurrency,
    useComparativoTotalsLocal
} from '../Utils/ComparativoFlujoCajaComponents';
import { TYPE_OF_FLOW } from '../../../../../../utility/constants';
import { renderCurrency, renderDollar, renderField, renderFieldDisabled } from '../../../../Utils/renderField/renderField';
import Swal from 'sweetalert2';
const alerta = require('assets/img/icons/alerta_roja.png');

const TableRow = ({ system, index, fields, change, currency, ...props }) => {
    const currentField = fields.get(index);
    const isManual = currentField.is_manual;
    const isSalida = currentField.type_of_flow === TYPE_OF_FLOW.SALIDAS;
    
    const calculateTotalForRow = () => {
        const systemData = fields.get(index);
        if (!systemData) return 0;

        let totalForRow = months.reduce((total, month) => {
            const value = parseFloat(systemData[month]) || 0;
            return total + value;
        }, 0);
        
        fields.get(index).total = totalForRow;
        return totalForRow;
    };

    const getTextColor = () => {
        return isSalida ? { color: '#dc3545' } : {};
    };

    const eliminarFilaManual = () => {
        if (isManual) {
            const currentRow = fields.get(index);
            const isNew = currentRow.isNew;
            
            if (isNew) {
                // Si es nuevo, solo quitar del FieldArray
                fields.remove(index);
            } else if (currentRow.id && props.eliminarRegistro) {
                // Si tiene ID (está guardado), mostrar confirmación y eliminar de la base de datos
                const SwalMod = Swal.mixin({
                    customClass: {
                        confirmButton: 'btn btn-primary btn-modal',
                        cancelButton: 'btn btn-outline-dark mr-5 btn-modal',
                        content: '',
                        title: 'darck',
                        popup: 'custom-modal-size',
                    },
                    buttonsStyling: false,
                    showCloseButton: true,
                    focusConfirm: false,
                    focusCancel: false,
                    padding: "1em",
                });

                SwalMod.fire({
                    title: 'ADVERTENCIA',
                    html: 'Si eliminas el registro perderás toda la información.<br/>¿Estás seguro de continuar?',
                    imageUrl: alerta,
                    imageAlt: 'Alerta',
                    showCancelButton: true,
                    confirmButtonText: 'CONTINUAR',
                    cancelButtonText: 'CANCELAR',
                    reverseButtons: true,
                }).then((result) => {
                    if (result.value) {
                        // Solo eliminar de DB - el dispatch(read()) se encarga de refrescar el formulario
                        props.eliminarRegistro(currentRow.id);
                    }
                });
            } else {
                // Fallback: quitar del FieldArray
                fields.remove(index);
            }
        }
    };

    React.useEffect(() => {
        change(`${system}.is_dollar`, currency === 'USD');
    }, [currency, system, change]);

    return (
        <React.Fragment>
            <tr>
                <td className='first-column table-form-fixed-cell-left-0' style={getTextColor()}>
                    <Field
                        name={`${system}.name`}
                        type="text"
                        component={isManual ? renderField : renderFieldDisabled}
                        className="form-control"
                        placeholder='Nuevo'
                        style={getTextColor()}
                    />
                </td>
                <td className='table-form-fixed-cell-left-1 text-right-padded' style={getTextColor()}>
                    {formatComparativoCurrency(calculateTotalForRow(), currency, calculateTotalForRow() < 0)}
                </td>

                {months.map((month) => {
                    return (
                        <td key={month} style={getTextColor()}>
                            <Field
                                name={`${system}.${month}`}
                                type="text"
                                component={currency === 'USD' ? renderDollar : renderCurrency}
                                placeholder={fields.get(index).is_dollar ? '$ 0.00' : 'Q 0.00'}
                                style={isSalida ? {
                                    color: '#dc3545',
                                    fontWeight: '500',
                                    borderColor: '#dc3545',
                                    backgroundColor: '#fff5f5'
                                } : {}}
                                isSalida={isSalida}
                                onBlur={(event) => {
                                    let currentValue = parseFloat(fields.get(index)[month]) || 0;
                                
                                    if (isSalida && currentValue > 0) {
                                        currentValue = -currentValue;
                                        change(`${system}.${month}`, currentValue);
                                        fields.get(index)[month] = currentValue;
                                    }
                                    calculateTotalForRow();
                                }}
                            />
                        </td>
                    );
                })}

                <td className="text-center sin-borde-top">
                    {isManual &&
                        <div>
                            <img src={require('assets/img/icons/Accion-Eliminar.svg')} alt="Borrar"
                                title="Borrar" className="action_img"
                                onClick={eliminarFilaManual}
                            />
                        </div>
                    }
                </td>
            </tr>
        </React.Fragment>
    );
};

const GenericTable = (props) => {
    const { fields, currency, allValues } = props;

    const [collapsed, setCollapsed] = React.useState(false);
    
    // Usar cálculos locales
    const localTotals = useComparativoTotalsLocal(allValues);

    // Separar campos por tipo usando la función mejorada
    const { ingresos, salidas } = separateFieldsByType(fields);

    // Calcular totales para mostrar en headers
    const totalIngresos = localTotals ? localTotals.financierosEntradas.total : 0;
    const totalSalidas = localTotals ? localTotals.financierosSalidas.total : 0;
    const diferencia = totalIngresos - totalSalidas;

    return (
        <div className=" col-sm-12 form-group np-r p-0">
            <div className='table-responsive'>
                <table className="table mb-0 table-striped">
                    {/* Header principal colapsable */}
                    {renderCollapsibleSectionHeader(
                        'FLUJOS FINANCIEROS',
                        '#e8f4fd',
                        '#0c5460',
                        [],
                        collapsed,
                        () => setCollapsed(!collapsed),
                        diferencia
                    )}
                    
                    <tbody style={{ backgroundColor: "#F0FFC7" }}>
                        {!collapsed && (
                            <React.Fragment>
                                {/* Fila de totales de INGRESOS */}
                                {renderSectionTotalRow(
                                    'INGRESOS',
                                    '#c3e6cb',
                                    '#155724',
                                    totalIngresos,
                                    localTotals ? localTotals.financierosEntradas : {},
                                    currency
                                )}

                                {/* Renderizar ingresos */}
                                {ingresos.map(({ field, index }) =>
                                    <TableRow
                                        key={`ingreso-${index}`}
                                        system={`financial.${index}`}
                                        index={index}
                                        fields={fields}
                                        change={props.change}
                                        currency={currency}
                                        {...props}
                                    />
                                )}
                                
                                {/* Fila de totales de SALIDAS */}
                                {renderSectionTotalRow(
                                    'SALIDAS',
                                    '#f5c6cb',
                                    '#721c24',
                                    -totalSalidas, // Negativo para mostrar correctamente
                                    localTotals ? Object.fromEntries(
                                        months.map(month => [month, -(localTotals.financierosSalidas[month] || 0)])
                                    ) : {},
                                    currency
                                )}

                                {/* Renderizar salidas */}
                                {salidas.map(({ field, index }) =>
                                    <TableRow
                                        key={`salida-${index}`}
                                        system={`financial.${index}`}
                                        index={index}
                                        fields={fields}
                                        change={props.change}
                                        currency={currency}
                                        {...props}
                                    />
                                )}

                                {/* FILA DE DIFERENCIA GENERAL */}
                                {renderDifferenceRow(
                                    `${currency === 'USD' ? 'DIFERENCIA $' : 'DIFERENCIA Q'}`,
                                    diferencia,
                                    localTotals ? localTotals.financierosNeto : {},
                                    currency
                                )}
                            </React.Fragment>
                        )}
                        
                        {/* Mostrar solo totales cuando está colapsado */}
                        {collapsed && (
                            <React.Fragment>
                                {renderSectionTotalRow(
                                    'INGRESOS',
                                    '#c3e6cb',
                                    '#155724',
                                    totalIngresos,
                                    localTotals ? localTotals.financierosEntradas : {},
                                    currency
                                )}
                                {renderSectionTotalRow(
                                    'SALIDAS',
                                    '#f5c6cb',
                                    '#721c24',
                                    -totalSalidas,
                                    localTotals ? Object.fromEntries(
                                        months.map(month => [month, -(localTotals.financierosSalidas[month] || 0)])
                                    ) : {},
                                    currency
                                )}
                                {renderDifferenceRow(
                                    'DIFERENCIA',
                                    diferencia,
                                    localTotals ? localTotals.financierosNeto : {},
                                    currency
                                )}
                            </React.Fragment>
                        )}
                    </tbody>
                </table>
            </div>
            
            {/* Botones para agregar ingresos y salidas */}
            {!collapsed && (
                <AddTransactionButtons 
                    fields={fields} 
                    setCollapsed={setCollapsed} 
                    transactionType="FINANCIAL"
                />
            )}
        </div>
    );
};

const FlujosFinancierosTable = (props) => {
    const { dollar_exchange, allValues, change, eliminarRegistro, currency } = props;

    return (
        <div>
            <FieldArray
                name={`financial`}
                dollar_exchange={dollar_exchange}
                component={GenericTable}
                allValues={allValues}
                change={change}
                eliminarRegistro={eliminarRegistro}
                currency={currency}
            />
        </div>
    );
};

export default FlujosFinancierosTable;
